import { useState, useCallback, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export function useLocationSharing(locationSharedAt?: string | null) {
  const { user } = useAuth();
  const [isSharing, setIsSharing] = useState(false);
  const [permissionState, setPermissionState] = useState<PermissionState | null>(null);
  const [locationGranted, setLocationGranted] = useState(false);

  // Check browser permission on mount
  useEffect(() => {
    if (!navigator.permissions) return;
    navigator.permissions.query({ name: "geolocation" as PermissionName }).then((result) => {
      setPermissionState(result.state);
      result.onchange = () => setPermissionState(result.state);
    }).catch(() => {});
  }, []);

  // Derive locationGranted from permission + DB timestamp
  useEffect(() => {
    if (permissionState === "granted" && locationSharedAt) {
      setLocationGranted(true);
    }
  }, [permissionState, locationSharedAt]);

  // Auto-share silently when permission already granted but no DB record yet
  useEffect(() => {
    if (permissionState === "granted" && !locationSharedAt && user && !isSharing) {
      shareLocationInternal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissionState, locationSharedAt, user]);

  const shareLocationInternal = useCallback(async (silent = false) => {
    if (!navigator.geolocation || !user) return;

    setIsSharing(true);
    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        });
      });

      const { latitude, longitude } = position.coords;

      const { data: student } = await supabase
        .from("students")
        .select("id")
        .eq("user_id", user.id)
        .single();

      if (!student) {
        if (!silent) toast.error("No student record found");
        return;
      }

      const { error } = await supabase
        .from("students")
        .update({
          latitude,
          longitude,
          location_shared_at: new Date().toISOString(),
        })
        .eq("id", student.id);

      if (error) throw error;

      setLocationGranted(true);
      if (!silent) toast.success("Location shared successfully");
    } catch (err: any) {
      if (!silent) {
        if (err.code === 1) {
          toast.error("Location permission denied. Please enable it in your browser settings.");
          setPermissionState("denied");
        } else if (err.code === 2) {
          toast.error("Unable to determine your location");
        } else if (err.code === 3) {
          toast.error("Location request timed out");
        } else {
          toast.error("Failed to share location");
        }
      }
    } finally {
      setIsSharing(false);
    }
  }, [user]);

  const shareLocation = useCallback(() => shareLocationInternal(false), [shareLocationInternal]);

  return { shareLocation, isSharing, permissionState, locationGranted };
}
